import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Label, List, Modal } from 'semantic-ui-react';

import { bem } from '../../core/design/bem';
import { GroupWithInvited } from '../types';
import { formatSlotRange, getProgramLabel } from '../utils';
import InvitedList from './InvitedList';

const css = bem('GroupDetailsModal');
const translationPrefix = 'group-visits';

type GroupDetailsModalProps = {
  group: GroupWithInvited;
  onClose: () => void;
  onEdit: (group: GroupWithInvited) => void;
};

type ProgramEntry = { label: string; type: 'demo' | 'conference' };

const GroupDetailsModal = ({ group, onClose, onEdit }: GroupDetailsModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { name, inviterName, guide, slot, invited, demos = [], conferences = [] } = group;
  const title = name || inviterName || t(`${translationPrefix}.card.untitled`);

  const program: ProgramEntry[] = [
    ...demos.map((d): ProgramEntry => ({ label: getProgramLabel(d), type: 'demo' })),
    ...conferences.map((c): ProgramEntry => ({ label: getProgramLabel(c), type: 'conference' })),
  ].filter((entry) => entry.label);

  return (
    <Modal open onClose={onClose} closeIcon size="large" className={css().toString()}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content scrolling>
        <div className={css('meta').toString()}>
          {inviterName && (
            <Label basic>
              <Icon name="user" />
              {inviterName}
            </Label>
          )}
          {slot && (
            <Label basic>
              <Icon name="calendar alternate outline" />
              {formatSlotRange(slot)}
            </Label>
          )}
          {guide && (
            <Label basic>
              <Icon name="compass outline" />
              {guide}
            </Label>
          )}
        </div>

        <h4 className={css('subtitle').toString()}>{t(`${translationPrefix}.details.program`)}</h4>
        {program.length > 0 ? (
          <List divided relaxed className={css('program').toString()}>
            {program.map((entry, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <List.Item key={`${entry.type}-${index}`} className={css('program-item').toString()}>
                <span className={css('program-index').toString()}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <List.Content className={css('program-label').toString()}>
                  {entry.label}
                </List.Content>
                <Label size="small" basic color={entry.type === 'demo' ? 'blue' : 'purple'}>
                  {t(`${translationPrefix}.details.type.${entry.type}`)}
                </Label>
              </List.Item>
            ))}
          </List>
        ) : (
          <div className={css('empty').toString()}>
            {t(`${translationPrefix}.details.no-program`)}
          </div>
        )}

        <h4 className={css('subtitle').toString()}>
          {t(`${translationPrefix}.details.guests`, { count: invited.length })}
        </h4>
        {invited.length > 0 ? (
          <InvitedList invited={invited} />
        ) : (
          <div className={css('empty').toString()}>
            {t(`${translationPrefix}.details.no-guest`)}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button primary onClick={() => onEdit(group)}>
          <Icon name="pencil" />
          {t('btn.edit')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default GroupDetailsModal;
